import { S, announce } from './state';
import * as A from './actions';
import * as Writes from './mapWrites';
import { getMapVersion } from '../api/map';
import { bootAtLeast, getStoreState } from '../store';
import type { NodeId, TagId } from '../lib/types';

/**
 * STAYING CURRENT WITH OTHER PEOPLE — the quiet refresh that replaced `alert()` + reload.
 *
 * Until V0.34 a client learned that somebody else had saved in exactly one way: its own next
 * write came back 409, and `boot.ts` answered with a browser popup and `location.reload()`.
 * That threw away the camera, every open branch, the open document and any unsaved popup
 * draft — for a conflict the user had no part in and, because view-only actions used to write,
 * usually had not caused. This module is the other half of the fix: the map is refetched IN
 * PLACE, the user's place in it is put back, and a small banner says what happened.
 *
 * ── THE IMPORTS GO ONE WAY ───────────────────────────────────────────────────────────────
 * `mapSync -> actions/state/mapWrites/store`, and nothing in `actions.ts` imports back. That
 * is why there is no `configure*` handshake here: the seams this needs (`rebaseStructural`,
 * `dropPendingStructural`, `structuralSyncBusy`, `openBranchIds`, `ConflictReport`) are all
 * ordinary exports, and `boot.ts` is the only module that refers to this one.
 *
 * ── WHAT A REFRESH IS, AND WHAT IT DELIBERATELY IS NOT ───────────────────────────────────
 * It is `loadMapFromServer()` with the view captured before and restored after. It is NOT
 * boot. Nothing here re-runs the boot state machine (`bootPhase` stays `'ready'`, which is
 * also what keeps `viewWritesAllowed()` true for the final `saveViewState()`), remounts a
 * region, re-establishes the session, reconnects presence, touches the router, or calls
 * `restoreViewState()`, `fit()`, `centerOnNodes()` or `save()`.
 *
 * THREE OF THOSE EXCLUSIONS ARE NOT TIDINESS:
 *
 *  - **`restoreViewState()` cannot be reused mid-session.** `readViewRecord()` DELETES a
 *    previous-work-day record as it reads it, so a refresh would consume the record; and the
 *    router's `explicitFilters` latch is set once at boot and never reset, so a session that
 *    arrived on `?tag=X` would skip restoring tags forever after. Hence an in-memory capture.
 *  - **`autoLayout()` must not run.** It would turn a passive refresh into this client
 *    publishing a relayout nobody asked for. A refresh adopts the server's positions verbatim.
 *  - **`applyPermissions()` must not run** — it toggles the presence socket and forces
 *    `setEditing(false)`. `refreshHiddenSets()` IS required, because hidden tags expand
 *    through `tagEdges`, which the load replaces wholesale.
 *
 * The open node popup and the doc overlay survive a refresh, and it is worth saying exactly
 * why, because the walkthrough that found V0.45's popup defects blamed this file for one it
 * does not cause. `NodePopup` keys its body on `${id}:${seq}` and bumps `seq` only inside
 * `openPopup()`, which nothing here calls; `loadMapFromServer()` replaces `S.nodes` and
 * `S.edges` in one assignment each, so there is no intermediate state to be caught in either.
 * An unsaved popup draft is therefore untouched by a refresh — which is precisely why neither
 * an open popup nor an open document blocks one.
 *
 * What DID discard a draft was `openPopup()` being used as "redraw this popup" by four call
 * sites of its own. `refreshPopup()` is that verb now (V0.45), and the popup additionally
 * routes a same-node `open()` to it while a draft is dirty. Nothing in this file changed.
 *
 * ── RETRY, NEVER DROP; AND SELF-SUPPRESS ─────────────────────────────────────────────────
 * A refresh that arrives while the user is dragging, renaming, linking or owing a write is
 * postponed on an 800 ms tick rather than thrown away, because the reason it is blocked is
 * exactly the reason it will matter. The tick self-suppresses once `S.dataVersion` has reached
 * the version that was wanted — which is what stops a client refreshing because of its OWN
 * write, since the server broadcasts `mapChanged` to everybody including the originator (their
 * other tabs are just as stale, and only the originating tab can tell).
 *
 * ── `S.dataVersion` IS NEVER SET FROM A 409 BODY ─────────────────────────────────────────
 * `currentDataVersion` is the refresh TARGET and nothing else. Adopting it would let the
 * single-flight queue's trailing run — which `settle()` starts a microtask BEFORE `onConflict`
 * fires — re-send the already-rejected change on top of the work that rejected it.
 */

/* ------------------------------------------------------------------------- the knobs */

/** How often a blocked or failed refresh re-asks. Deliberately not a poll: it only runs while
 *  a version is actually wanted, and stops the moment that version is reached. */
const TICK_MS = 800;
/** No more than one refetch per second however many `mapChanged` messages arrive. */
const MIN_REFRESH_INTERVAL_MS = 1000;
/** The "someone else changed the map" banner is informational and goes away by itself. */
const REFRESHED_NOTICE_TTL_MS = 8000;
/** After this many consecutive failures the tick stops re-arming; the next `mapChanged`,
 *  focus or visibility change starts it again. A dead server must not be polled forever. */
const MAX_CONSECUTIVE_FAILURES = 3;

/* ------------------------------------------------------------------ what blocks a refresh */

/**
 * Why a refresh cannot run right now, or `null` for "go ahead".
 *
 * ORDER IS THE ANSWER, not a preference: the first true reason is reported, so the caller and
 * the tests agree on one deterministic name per situation. An open popup, an open document and
 * an open doc editor are NOT blockers — see the header on why they survive a refresh intact.
 */
export type RefreshBlocker =
  | 'not-ready'
  | 'sync-busy'
  | 'edits-owed'
  | 'dragging'
  | 'renaming'
  | 'linking';

export function refreshBlockedBy(): RefreshBlocker | null {
  if (!S.currentUser || !bootAtLeast(S.bootPhase, 'ready')) return 'not-ready';
  // A write is in the air, or a trailing run is already queued behind it. Replacing the map
  // underneath that would make the delta it is about to build a diff against a map it never
  // saw.
  if (Writes.structuralSyncBusy()) return 'sync-busy';
  // Everything this client still owes the server. The position marks are in here as well as
  // the three translation/AI sets: a refresh rebases the baseline onto the server's own
  // coordinates, after which a marked-but-unsent position diffs to nothing and is lost.
  if (
    Writes.dirtyNodeTranslations.size ||
    Writes.dirtyTagTranslations.size ||
    Writes.dirtyAiFields.size ||
    Writes.dirtyNodePositions.size ||
    A.serverSyncPending()
  ) {
    return 'edits-owed';
  }
  const st = getStoreState();
  if (st.dragNodeId) return 'dragging';
  if (st.renamingId) return 'renaming';
  if (st.linkArmed || st.linkFromId) return 'linking';
  return null;
}

/* -------------------------------------------------------------------------- the notice */

let noticeTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Set the banner, honouring one precedence rule in both directions.
 *
 * `edit-rejected` always wins — it is the only one that says the user LOST something, and it
 * stays until dismissed. `map-refreshed` never replaces an existing notice, so a deep-link
 * notice from boot ("that page isn't available to you") is not clobbered by a refresh that
 * happened to land a second later, and a burst of 409s produces exactly one banner.
 */
function setNotice(
  kind: 'map-refreshed' | 'edit-rejected' | 'edit-forbidden' | 'maintenance',
  detail: string | null,
): void {
  if (kind === 'map-refreshed' && S.routeNotice) return;
  // `maintenance` (V0.48) is louder than a refresh but quieter than a loss: it may replace an
  // informational notice, and never one that says work was lost or the session ended. Somebody
  // whose change was just refused needs that sentence more than they need to know why the next
  // request also failed — and the restore banner will still be there to raise afterwards.
  if (kind === 'maintenance' && (S.routeNotice === 'edit-rejected' || S.routeNotice === 'edit-forbidden' || S.routeNotice === 'session-ended')) {
    return;
  }
  if (kind === S.routeNotice && detail === S.noticeDetail) return; // already saying this
  S.routeNotice = kind;
  S.noticeDetail = detail;
  if (noticeTimer) {
    clearTimeout(noticeTimer);
    noticeTimer = null;
  }
  if (kind === 'map-refreshed') {
    noticeTimer = setTimeout(() => {
      noticeTimer = null;
      if (S.routeNotice !== 'map-refreshed') return; // dismissed, or superseded
      S.routeNotice = null;
      S.noticeDetail = null;
      announce();
    }, REFRESHED_NOTICE_TTL_MS);
  }
  announce();
}

/* ------------------------------------------------------------------ capture and restore */

interface CapturedView {
  openIds: NodeId[];
  activeTags: TagId[];
  contentHits: NodeId[];
  chatHighlight: NodeId[];
  camera: { x: number; y: number; k: number };
}

/**
 * Everything about the user's place in the map that a refetch would otherwise destroy.
 *
 * Taken SYNCHRONOUSLY, before any await: between deciding to refresh and the response landing
 * the user may pan, and the camera that is put back has to be the one that was on screen when
 * the refresh started rather than a value read after the map was already replaced.
 *
 * `openBranchIds()` is the same function the persisted view record uses, so the capture and
 * the record can never disagree about what "open" means.
 */
function captureView(): CapturedView {
  return {
    openIds: A.openBranchIds(),
    activeTags: [...S.activeTags],
    contentHits: [...S.searchContentHits],
    chatHighlight: [...S.chatHighlight],
    camera: A.mapGetCamera(),
  };
}

/**
 * Put the captured view back, re-validated against the map that just arrived.
 *
 * Every id is checked again: a node somebody else deleted, or a tag that has become hidden for
 * this user's group since the capture, is skipped individually rather than taking the rest of
 * the selection with it — the rule `restoreViewState()` already follows for the stored record.
 *
 * `applySearch()` is deliberately NOT called. `searchText`/`searchQuery` are untouched by the
 * load, `computeForceOpen()` already folds the query in, and `applySearch()` would clear the
 * content hits this is restoring, call `saveViewState()` in the middle of a restore and fire
 * `Router.syncUrl('filter')` for a navigation that never happened.
 */
function restoreCapturedView(cap: CapturedView): void {
  for (const id of cap.openIds) {
    const n = S.nodes[id];
    if (n && !n.root && !A.permHidden(id)) n.collapsed = false;
  }
  S.activeTags = new Set(cap.activeTags.filter((t) => S.tags[t] && !A.tagHidden(t)));
  S.searchContentHits = new Set(cap.contentHits.filter((id) => S.nodes[id]));
  S.chatHighlight = new Set(cap.chatHighlight.filter((id) => S.nodes[id]));
  // Derived, never persisted, and it depends on all four of the above.
  S.forceOpen = A.computeForceOpen();
  A.mapSetCamera(cap.camera);
}

/* -------------------------------------------------------------------------- the refresh */

/**
 * Refetch the map and put the user back where they were. Answers whether it worked.
 *
 * On a failed load the state is left exactly as it was: a client that cannot reach the server
 * should keep showing the map it has, not empty itself.
 */
export async function refreshMapInPlace(): Promise<boolean> {
  const captured = captureView();
  try {
    await A.loadMapFromServer();
  } catch (err) {
    console.warn('[mapSync] refresh failed; keeping the map we have', err);
    return false;
  }
  A.refreshHiddenSets(); // hidden tags expand through tagEdges, which the load just replaced
  restoreCapturedView(captured);
  A.render();
  A.renderTags();
  A.saveViewState(); // localStorage only — the view moved for nobody but this user
  // The heat is computed over what the refresh just replaced, so it is re-asked — quietly,
  // like the refresh itself, and only while the mode is actually on (V0.39).
  if (S.heatmapOn) void A.loadMapHealth();
  return true;
}

/* ------------------------------------------------------------------------------ the tick */

/** The version this client is trying to reach. 0 means "nothing wanted". */
let wantedVersion = 0;
/**
 * "Refresh even though the version is current."
 *
 * A permission change moves no map content, so `mapMeta.dataVersion` does not move either —
 * and yet what this client is ENTITLED to may be different, because `GET /api/map` withholds
 * nodes (V0.43). That cannot be expressed as a wanted version: `runTick` self-suppresses on
 * `S.dataVersion >= wantedVersion`, which is exactly the comparison that stops a client
 * refreshing for its own writes, and inventing a version to want would break it. Hence a
 * second, independent reason to refresh.
 */
let permWanted = false;

let tickTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<boolean> | null = null;
let lastRefreshAt = 0;
let failures = 0;

function armTick(): void {
  if (tickTimer) return;
  tickTimer = setTimeout(runTick, TICK_MS);
}

function stopWanting(): void {
  wantedVersion = 0;
  permWanted = false;
  failures = 0;
}

function runTick(): void {
  tickTimer = null;
  // SELF-SUPPRESSION. Our own write's response, or a refresh that already landed, moved
  // `S.dataVersion` to (or past) what was wanted — so there is nothing to fetch. This one
  // comparison is what stops a client refreshing because of a change it made itself.
  // ...unless a PERMISSION change is what we are here for, which the version cannot express.
  if (!permWanted && S.dataVersion >= wantedVersion) return stopWanting();

  const blocked = refreshBlockedBy();
  if (blocked) return armTick(); // postponed, never dropped

  const since = Date.now() - lastRefreshAt;
  if (since < MIN_REFRESH_INTERVAL_MS) return armTick();

  void runRefresh();
}

function runRefresh(): Promise<boolean> {
  if (inFlight) return inFlight;
  lastRefreshAt = Date.now();
  const run = refreshMapInPlace().then(
    (ok) => {
      inFlight = null;
      if (!ok) {
        failures++;
        if (failures < MAX_CONSECUTIVE_FAILURES) armTick();
        return false;
      }
      failures = 0;
      permWanted = false;
      // Only now, and only if nothing louder is already on screen: `edit-rejected` outranks
      // this, and so does a boot-time deep-link notice.
      setNotice('map-refreshed', null);
      // Somebody wrote again while we were fetching — the tick will catch that up.
      if (S.dataVersion < wantedVersion) armTick();
      else stopWanting();
      return true;
    },
    (err) => {
      inFlight = null;
      failures++;
      console.warn('[mapSync] refresh threw', err);
      if (failures < MAX_CONSECUTIVE_FAILURES) armTick();
      return false;
    },
  );
  inFlight = run;
  return run;
}

/* ------------------------------------------------------------------------- the entry points */

/**
 * "This user's permissions changed, so refetch the map."
 *
 * Called by `actions.reapplyCurrentUser()` through a sink wired in `boot.ts` — `actions.ts` may
 * not import this file (see the header's one-way rule), and only when the hidden sets actually
 * moved, because that is all the payload depends on.
 *
 * GOES THROUGH THE TICK rather than calling `runRefresh()` directly. `refreshBlockedBy()` is
 * what stops a refetch rebasing the delta baseline underneath a write that is still in flight,
 * or discarding a position mark that has not been sent yet. `noteWriteForbidden()` below gets
 * away with a direct call only because it has just dropped the pending delta on purpose; that
 * is not true here, where the user may be mid-edit and losing nothing.
 */
export function notePermissionsChanged(): void {
  permWanted = true;
  failures = 0;
  armTick();
}

/**
 * "The map is at version V somewhere else." From the presence socket's `mapChanged`, and from
 * the focus/visibility fallback below.
 *
 * A version at or below ours is old news — our own write's echo, most often — and costs
 * nothing at all: no request, no timer, no notice.
 */
export function noteRemoteVersion(version: number): void {
  if (typeof version !== 'number' || !Number.isFinite(version)) return;
  if (version <= S.dataVersion) return;
  if (version > wantedVersion) {
    wantedVersion = version;
    failures = 0; // a newer fact deserves a fresh set of attempts
  }
  armTick();
}

/**
 * One of OUR writes was refused because somebody else saved first.
 *
 * THE NOTICE IS SET SYNCHRONOUSLY, FIRST. The refresh that follows may itself fail, and the
 * user must be told they lost a change either way — "the map now shows their version" is a
 * promise the refresh keeps, but "your change was not saved" is already true.
 *
 * The rejected change is then PARKED rather than retried: `dropPendingStructural()` drops the
 * position marks and the in-flight record, and the rebase that the refetch performs moves the
 * baseline to what the server actually has, which parks the content half. Re-sending would put
 * the refused change back on top of the work that refused it.
 */
export function noteWriteRejected(report: Writes.ConflictReport): void {
  setNotice('edit-rejected', null);
  Writes.dropPendingStructural();
  // NEVER `S.dataVersion = report.serverVersion`. The body's number is a refresh TARGET; the
  // `+ 1` floor covers a 409 whose body did not say, so a refresh still happens.
  const target = Math.max(report.serverVersion ?? 0, S.dataVersion + 1);
  if (target > wantedVersion) {
    wantedVersion = target;
    failures = 0;
  }
  armTick();
}

/**
 * One of OUR writes was refused for MISSING PERMISSION BRICKS (V0.37), and the server named
 * them. Reachable only through a race — the affordance sweep keeps a brick-less user from
 * enqueuing the family at all, so this fires when a brick was withdrawn behind an affordance
 * already drawn (an admin edited the group mid-session).
 *
 * Same shape as `noteWriteRejected` — notice first, park the refused change — with one
 * difference that matters: the server's version DID NOT MOVE, so there is no wanted version
 * to tick towards. A single in-place refresh adopts the server's state (which rebases the
 * baseline and so parks the refused content), and the banner names the bricks; arming the
 * version tick instead would poll forever for a version that never arrives.
 */
export function noteWriteForbidden(missing: string[]): void {
  setNotice('edit-forbidden', missing.length ? `(Missing: ${missing.join(', ')})` : null);
  Writes.dropPendingStructural();
  void runRefresh();
}

/**
 * The fallback, and the dropped-socket safety net: ask the server what version it is on.
 *
 * Wired to `focus` and `visibilitychange` for EVERYONE, not only users without
 * `SHOW_PRESENCE` — a socket that quietly died leaves a presence user exactly as stale as one
 * who never had a socket. There is NO interval poll: this codebase's stance on polling is
 * explicit (`state.ts`, `FeedBell.tsx`), and a tab nobody is looking at has nothing to refresh.
 *
 * Failures are swallowed. This runs on every focus, and `e2e/13-offline.spec.ts` watches the
 * console as well as the wire.
 */
export async function checkMapVersion(): Promise<void> {
  if (!S.currentUser || !bootAtLeast(S.bootPhase, 'ready')) return;
  try {
    const { dataVersion } = await getMapVersion();
    // An answer at all is the fact that ends a maintenance banner — see `noteServerBack`.
    noteServerBack();
    noteRemoteVersion(dataVersion);
  } catch {
    /* offline, signed out, or the server is restarting — the next focus asks again */
  }
}

/* ------------------------------------------------------------------------- maintenance */

/**
 * Has this tab seen the server refuse everything since the last time it answered?
 *
 * Module state rather than a look at `S.routeNotice`, because the banner can be dismissed by
 * the reader while the restore is still running, and a dismissed banner must not turn the
 * refresh that follows into a no-op. What is owed is a refetch, not a sentence.
 */
let maintenanceSeen = false;

/**
 * "An administrator is restoring a backup" — from the 503 sink wired in `boot.ts` (V0.48).
 *
 * Reached from ANY route, not just this file's: during a restore a popup save, a search and a
 * version poll all answer the same way, and without one home for that a restore looks like a
 * scatter of unrelated failures. Firing repeatedly is free — `setNotice` returns early when it
 * is already saying this.
 */
export function noteMaintenance(): void {
  if (!S.currentUser || !bootAtLeast(S.bootPhase, 'ready')) return;
  maintenanceSeen = true;
  setNotice('maintenance', null);
}

/**
 * "The server answered again."
 *
 * Called on any successful version check — from focus, from a visibility change, and from the
 * presence socket reconnecting, which is the one that matters: a restore closes every socket
 * (code 4001), so the reconnect is the earliest honest evidence the server is back, and it
 * arrives without the reader touching anything.
 *
 * IT FORCES A REFRESH THROUGH `permWanted`, NOT A WANTED VERSION, and that is the whole
 * subtlety of this function. A restore replaces `MapMeta`, so the version the server now
 * reports is usually LOWER than the one this tab holds — a same-installation round trip
 * restores the old number, an older bundle a smaller one — and `noteRemoteVersion` correctly
 * ignores anything at or below `S.dataVersion`. The map underneath has nevertheless been
 * replaced wholesale. `permWanted` is the existing way to say "refetch even though the version
 * is current", written for permission changes, and this is the same shape of fact: what the
 * server will send is different for a reason the version cannot express.
 *
 * NOTE what this deliberately does not do: `refreshMapInPlace` does not re-apply permissions
 * (see this file's header), so a restored role reaches other tabs at their next reload rather
 * than now. The tab that ran the restore reloads itself; for everybody else a stale gate for a
 * few minutes is the lesser evil against tearing the app down under someone mid-sentence.
 */
export function noteServerBack(): void {
  if (!maintenanceSeen) return;
  maintenanceSeen = false;
  if (S.routeNotice === 'maintenance') {
    S.routeNotice = null;
    S.noticeDetail = null;
    announce();
  }
  permWanted = true;
  failures = 0;
  armTick();
}
